import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { of } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { Notification } from '@parkingless/interface';
import { AuthService } from '../../services/auth.service';
import { DataService } from '../../services/data.service';
import { NotificationService } from '../../services/firestore/notification.service';
import { UserService } from '../../services/firestore/user.service';
import { ToastService } from '../../services/ui/toast.service';
import {
  NotificationEntry,
  PendingSeen,
  decidedNotification,
  markAllRead,
  markableCount,
  mergeNotifications,
  newPendingServices,
  outcomeOf,
  pendingServiceEntries,
  readNotification,
  removeNotification,
  replaceNotification,
  unreadCount,
} from '../../services/notifications';

/**
 * The toolbar's notification bell.
 *
 * Rebuilt as a real feature: in the previous console this lived inside the theme's
 * "fixed plugin" settings panel, alongside the colour pickers, and would have been
 * deleted with it.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [
    DatePipe,
    MatBadgeModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      mat-icon-button
      [matMenuTriggerFor]="menu"
      matTooltip="Notifiche"
      aria-label="Notifiche"
      (menuOpened)="opened.set(true)"
    >
      <!-- On the icon, not the button: a badge sits on its host's corner, and the
           button's is a touch target well clear of the bell it is about. -->
      <mat-icon
        [matBadge]="unread()"
        [matBadgeHidden]="unread() === 0"
        matBadgeColor="warn"
        matBadgeSize="small"
        aria-hidden="false"
      >
        notifications
      </mat-icon>
    </button>

    <mat-menu #menu="matMenu" class="notification-menu">
      <!-- Clicks are stopped so the menu stays open while acting on a notification. -->
      <div
        class="menu-header"
        role="group"
        tabindex="0"
        (click)="$event.stopPropagation()"
        (keydown)="$event.stopPropagation()"
      >
        <span>Notifiche</span>
        <button mat-button [disabled]="markable() === 0 || busy()" (click)="markRead()">
          Segna come lette
        </button>
      </div>

      <mat-divider />

      @if (!entries().length) {
        <p class="empty">Nessuna notifica.</p>
      } @else {
        @for (entry of entries(); track entry.notification.notificationId) {
          <div
            class="notification"
            role="group"
            tabindex="0"
            [class.unread]="!entry.notification.isRead"
            (click)="$event.stopPropagation()"
            (keydown)="$event.stopPropagation()"
          >
            <div class="notification-text">
              <strong>{{ entry.notification.title }}</strong>
              <span>{{ entry.notification.body }}</span>
              <span class="when">
                {{ entry.notification.creationDate.toDate() | date: 'dd/MM/yyyy HH:mm' }}
              </span>
            </div>

            <div class="notification-actions">
              @for (action of entry.notification.data.actionList ?? []; track action.action) {
                <button mat-button [disabled]="busy()" (click)="act(entry, action.action)">
                  {{ action.buttonName }}
                </button>
              }
              <!-- A job still waiting is not dismissed: it goes when it is answered. -->
              @if (entry.source.kind !== 'service') {
                <button
                  mat-icon-button
                  matTooltip="Elimina"
                  [disabled]="busy()"
                  (click)="dismiss(entry)"
                >
                  <mat-icon>close</mat-icon>
                </button>
              }
            </div>
          </div>
        }
      }
    </mat-menu>
  `,
  styles: `
    .menu-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 8px 16px;
      font-weight: 600;
    }

    .notification {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 8px 16px;
      max-width: 420px;
    }

    .notification.unread {
      background: rgb(33 150 243 / 8%);
    }

    .notification-text {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
    }

    .when {
      font-size: 11px;
      opacity: 0.6;
    }

    .notification-actions {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .empty {
      padding: 16px;
      opacity: 0.7;
    }
  `,
})
export class NotificationBellComponent {
  private readonly auth = inject(AuthService);
  private readonly data = inject(DataService);
  private readonly notifications = inject(NotificationService);
  private readonly users = inject(UserService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  protected readonly busy = signal(false);
  protected readonly opened = signal(false);
  /** Closed when a notification leads somewhere: the menu would stay over the page. */
  private readonly menuTrigger = viewChild(MatMenuTrigger);

  private readonly user = toSignal(this.data.user$, { initialValue: null });

  private readonly garageRoles = toSignal(this.data.garageRoles$, { initialValue: null });

  private readonly garageIds = computed(() =>
    (this.garageRoles() ?? []).map((entry) => entry.garage.garageId),
  );

  // The rules let this listen through on the garage claims, and a garage just created
  // is on screen before its claim is in the token. The refusal used to reach
  // `toSignal`, which rethrew it on every read, so the bell stayed broken until a
  // reload. Refused, the bell shows nothing for the moment, and listens again when the
  // session next refreshes the claims — which it does as soon as the backend stamps
  // the new one.
  private readonly garageDocuments = toSignal(
    toObservable(computed(() => this.garageIds().join(','))).pipe(
      switchMap((ids) =>
        this.auth.claimsRefreshed$.pipe(
          startWith(undefined),
          map(() => ids),
        ),
      ),
      switchMap((ids) =>
        ids
          ? this.notifications.watchForGarages(ids.split(',')).pipe(
              catchError((error: unknown) => {
                console.warn('[Notifications] garage notifications refused for now', error);
                return of([]);
              }),
            )
          : of([]),
      ),
    ),
    { initialValue: [] },
  );

  /** The jobs customers have asked for and no one has answered, in every garage. */
  private readonly pending = computed(() =>
    pendingServiceEntries((this.garageRoles() ?? []).map((entry) => entry.garage)),
  );

  protected readonly entries = computed(() =>
    mergeNotifications(this.user()?.notificationList, this.garageDocuments(), this.pending()),
  );

  protected readonly unread = computed(() => unreadCount(this.entries()));
  protected readonly markable = computed(() => markableCount(this.entries()));

  /** What was already waiting the last time the garages answered; null before they have. */
  private seen: PendingSeen | null = null;

  constructor() {
    // A request that arrives while the console is open is said, not only counted: the
    // badge changes in a corner nobody is looking at.
    effect(() => {
      if (this.garageRoles() === null) {
        this.seen = null;

        return;
      }
      const { fresh, seen } = newPendingServices(this.seen, this.garageIds(), this.pending());
      this.seen = seen;
      untracked(() => {
        for (const entry of fresh) {
          const link = entry.notification.data.actionList?.[0]?.action;
          this.toast.action(`Nuovo servizio da confermare: ${entry.notification.body}`, () => {
            if (link) {
              void this.router.navigateByUrl(link);
            }
          });
        }
      });
    });
  }

  /** Marks everything read, in the user's list and in every garage's. */
  protected async markRead(): Promise<void> {
    const user = this.user();
    const now = new Date();

    this.busy.set(true);
    try {
      const writes: Promise<void>[] = [];
      if (user && (user.notificationList ?? []).some((entry) => !entry.isRead)) {
        writes.push(
          this.users.update(user.userId, {
            notificationList: markAllRead(user.notificationList, now),
          } as never),
        );
      }
      for (const document of this.garageDocuments()) {
        if ((document.notificationList ?? []).some((entry) => !entry.isRead)) {
          writes.push(
            this.notifications.updateList(
              document.garageId,
              markAllRead(document.notificationList, now),
            ),
          );
        }
      }

      await Promise.all(writes);
    } catch (error) {
      this.toast.koNotification('Errore nell’aggiornamento delle notifiche');
      console.error('[Notifications] marking every notification read failed', error);
    } finally {
      this.busy.set(false);
    }
  }

  /** Acts on a notification's button: either navigate, or record the decision. */
  protected async act(entry: NotificationEntry, action: string): Promise<void> {
    // A job waiting is answered on the agenda, where it is: its button only goes there.
    if (entry.source.kind === 'service') {
      this.menuTrigger()?.closeMenu();
      await this.router.navigateByUrl(action);

      return;
    }

    const outcome = outcomeOf(entry.notification, action);
    if (outcome.kind === 'navigate') {
      this.menuTrigger()?.closeMenu();
      await this.router.navigateByUrl(outcome.path);

      return;
    }

    await this.write(entry, decidedNotification(entry.notification, outcome.status, new Date()));
  }

  /** Removes a notification from whichever list it belongs to. */
  protected async dismiss(entry: NotificationEntry): Promise<void> {
    await this.writeList(entry, (list) =>
      removeNotification(list, entry.notification.notificationId),
    );
  }

  /** Marks one notification read where it stands. */
  protected async read(entry: NotificationEntry): Promise<void> {
    await this.write(entry, readNotification(entry.notification, new Date()));
  }

  private write(entry: NotificationEntry, updated: Notification): Promise<void> {
    return this.writeList(entry, (list) => replaceNotification(list, updated));
  }

  private async writeList(
    entry: NotificationEntry,
    change: (list: readonly Notification[] | undefined) => Notification[],
  ): Promise<void> {
    this.busy.set(true);
    try {
      if (entry.source.kind === 'user') {
        const user = this.user();
        if (!user) {
          return;
        }
        await this.users.update(user.userId, {
          notificationList: change(user.notificationList),
        } as never);
      } else if (entry.source.kind === 'garage') {
        const garageId = entry.source.garageId;
        const document = this.garageDocuments().find((entry2) => entry2.garageId === garageId);
        await this.notifications.updateList(garageId, change(document?.notificationList));
      }
    } catch (error) {
      this.toast.koNotification('Errore nell’aggiornamento delle notifiche');
      console.error('[Notifications] read-state write failed', error);
    } finally {
      this.busy.set(false);
    }
  }
}
